import { useTranslation } from 'react-i18next';
import { ApiError } from 'shared/api';
import { searchRoles } from 'shared/api/entities';
import { Role } from 'shared/types';

import ServerSearchAutocomplete from '../ServerSearchAutocomplete';
import { ServerAutocompleteProps } from '../ServerSearchAutocomplete/ServerSearchAutocomplete';

const roleFetcher = async (query: string, signal?: AbortSignal): Promise<Role[] | ApiError> => {
    const response = await searchRoles(query, undefined, undefined, signal);

    if (response instanceof ApiError) {
        return response;
    }
    
    return response.items;
};

type RoleAutocompleteProps<Multiple extends boolean = false> = Omit<ServerAutocompleteProps<Role, Multiple>,
    'getOptionLabel' |
    'search'
> & {
    /** Prefetch suggestions on mount. Defaults to true. */
    preloadOnMount?: boolean;
    /** Prefetch suggestions when the dropdown is first opened instead of on mount. */
    preloadOnOpen?: boolean;
}

export default function RoleAutocomplete<Multiple extends boolean = false>({
    multiple,
    preloadOnMount = true,
    preloadOnOpen,
    ...rest
}: RoleAutocompleteProps<Multiple>) {
    const { t } = useTranslation('common');

    return (
        <ServerSearchAutocomplete<Role, Multiple>
            size="small"
            multiple={ multiple }
            label={ multiple ? t('commonEntities.roles') : t('commonEntities.role') }
            search={{
                fetcher: roleFetcher,
                minLength: 2,
                preloadOnMount,
                preloadOnOpen
            }}
            { ...rest }
        />
    );
}
